import { fabric } from 'fabric';
import Canvas from "./../Canvas";
import BoundingBox from "./BoundingBox";
import CanvasDebug from "./../../CanvasDebug";

/**
 * Represents an object of type fabric.Object on the canvas in the state of Vue.js.
 */
class CanvasObject {

    _isSelectable;

    /**
     * Canvas on which the object is displayed.
     * @type Canvas
     */
    _canvas;

    /**
     * Identification used to determine the object of type fabric.Object
     * rendered on the canvas that is the visual representation of this canvas object.
     * @type string
     */
    _id;

    /**
     * Position of the object on the y-axis relative to the canvas.
     * @type number
     */
    _top;
    _topIsManipulable;
    _topWhitelist;

    /**
     * Position of the object on the x-axis relative to the canvas.
     * @type number
     */
    _left;
    _leftIsManipulable;
    _leftWhitelist;

    /**
     * Position of the object on the y-axis relative to the BoundingBox.
     * @type number
     */
    _topBoundingBox;
    _topBoundingBoxIsManipulable;
    _topBoundingBoxWhitelist;

    /**
     * Position of the object on the x-axis relative to the BoundingBox.
     * @type number
     */
    _leftBoundingBox;
    _leftBoundingBoxIsManipulable;
    _leftBoundingBoxWhitelist;

    // TODO: implement bounding box relative top and left

    _minBoundingBoxLeft;
    _minBoundingBoxTop;
    _minBoundingBoxRight;
    _minBoundingBoxBottom;

    _maxBoundingBoxLeft;
    _maxBoundingBoxTop;
    _maxBoundingBoxRight;
    _maxBoundingBoxBottom;

    /**
     * Height of the object on the canvas.
     * @type number
     */
    _height;
    _heightIsManipulable;
    _heightWhitelist;

    /**
     * Width of the object on the canvas.
     * @type number
     */
    _width;
    _widthIsManipulable;
    _widthWhitelist;

    /**
     * Object scale factor on x-axis.
     * @type number
     */
    _scaleX;

    /**
     * Object scale factor on y-axis.
     * @type number
     */
    _scaleY;

    /**
     * Angle of rotation of an object (in degrees)
     * @type number
     */
    _angle;
    _angleIsManipulable;
    _angleWhitelist;

    /**
     * Is the position of the object partially or completely outside the bounding box.
     * @type boolean
     */
    _exceedsBoundingBox;

    /**
     * @type ?number
     */
    _zPosition;

    /**
     * A Object can be visible on web, print file or both.
     * 0 - visible on web and print file
     * 1 - visible on canvas (web browser)
     * 2 - add to print files
     * @type number
     */
    _visibility;

    /**
     * @type boolean
     */
    _deletable;

    /**
     * @type boolean
     */
    _locked;

    /**
     * Create a CanvasObject.
     * @param {Canvas} canvas Instance of the canvas on which the object is displayed.
     */
    constructor(canvas) {

        this._canvas = canvas;

        // The identification consists of 9 random characters and a timestamp when the instance was created.
        this._id = 'obj_' + Math.random().toString(36).substr(2, 9) + new Date().getTime();

        this._zPosition = this.getCanvas().getCanvasObjects().length;

        this._isProtected = false;

    }

    /**
     * Adapt this instance to the new values of the matching FabricObject properties on the canvas.
     * @return fabric.Object
     */
    readFromFabricObject() {

        /**
         * Instance of matching FabricObject on the Canvas.
         * @type fabric.Object
         */
        const fabricObject = this.getMatchingFabricObject();

        if (! fabricObject)
            return null;

        if (! fabricObject.isOnScreen())
            return fabricObject;

        if (this.exceedsPositionLimitsByResizing(fabricObject))
            return fabricObject;

        /**
         * Instance of BoundingBox from Canvas.
         * @type BoundingBox
         */
        const boundingBox = this.getCanvas().getFabricObjectById('boundingBox');

        // Matching properties that are easy to copy are with the same naming.
        const propertiesToCopy = [ 'left', 'top', 'width', 'height', 'angle', 'scaleX', 'scaleY' ];
        propertiesToCopy.forEach(
            property => this.set({ [property]: fabricObject[property] })
        );

        // Determine other properties.
        this.setExceedsBoundingBox(
            ! fabricObject.isContainedWithinObject(boundingBox)
        );
        this.setLeftBoundingBox(fabricObject.left - this.getCanvas().getBoundingBox().getLeft());
        this.setTopBoundingBox(fabricObject.top - this.getCanvas().getBoundingBox().getTop());
        //this.setWidth(fabricObject.getScaledWidth());

        return fabricObject;

    }

    /**
     *
     * @param {fabric.Object} canvasObject
     */
    exceedsPositionLimitsByResizing(canvasObject) {

        console.log({canvasObject});

        // left

        if (this._minBoundingBoxLeft) {
            if (this.getCanvas().convertMmInPixel(this._minBoundingBoxLeft) > canvasObject.left) {
                if (this.getScaledWidth() + 1 < canvasObject.getScaledWidth()) {
                    return true;
                }
            }
        }

        if (this._maxBoundingBoxLeft) {
            if (this.getCanvas().convertMmInPixel(this._maxBoundingBoxLeft) < canvasObject.left) {
                return true;
            }
        }

        // right

        if (this._minBoundingBoxRight) {
            if (this.getCanvas().convertMmInPixel(this._minBoundingBoxRight) > canvasObject.left + canvasObject.getScaledWidth()) {
                return true;
            }
        }

        if (this._maxBoundingBoxRight) {
            if (this.getCanvas().convertMmInPixel(this._maxBoundingBoxRight) < canvasObject.left + canvasObject.getScaledWidth()) {
                if (this.getScaledWidth() + 1 < canvasObject.getScaledWidth()) {
                    return true;
                }
            }
        }

        if (this._minBoundingBoxTop) {
            if (this.getCanvas().convertMmInPixel(this._minBoundingBoxTop) > canvasObject.top) {
                return true;
            }
        }

        if (this._maxBoundingBoxTop) {
            if (this.getCanvas().convertMmInPixel(this._maxBoundingBoxTop) < canvasObject.top) {
                return true;
            }
        }

        if (this._minBoundingBoxBottom) {
            if (this.getCanvas().convertMmInPixel(this._minBoundingBoxBottom) > canvasObject.top + canvasObject.getScaledHeight()) {
                return true;
            }
        }

        if (this._maxBoundingBoxBottom) {
            if (this.getCanvas().convertMmInPixel(this._maxBoundingBoxBottom) < canvasObject.top + canvasObject.getScaledHeight()) {
                return true;
            }
        }

        return false;

    }

    /**
     * Returns a new FabricObject that can be added to the canvas.
     * @returns {Promise<fabric.Object>}
     * @throws Will throw an error if the child not overrides the method.
     */
    createFabricObject() {

        throw `Method createFabricObject() must be implemented by the class ${this.constructor.name}.`;

    }

    /**
     * A post request is sent to the API and attempts to create the object.
     * @param {number} configurationViewId
     * @returns {Promise<void>}
     * @throws Will throw an error if the child not overrides the method.
     */
    async save(configurationViewId) {

        throw `Method save() must be implemented by the class ${this.constructor.name}.`;

    }

    /**
     * Sets default values for properties that use objects of any type.
     */
    initDefaultValues() {

        this.initAllIsManipulable();

        this.setLeft(0);
        this.setTop(0);
        this.setScaleX(1);
        this.setScaleY(1);
        this.setAngle(0);
        this.setIsLocked(false);

    }

    /**
     * Let modify all properties by default and the object is visible by default on web and print files.
     */
    initAllIsManipulable() {

        this.setTopIsManipulable(true);
        this.setLeftIsManipulable(true);
        this.setTopBoundingBoxIsManipulable(true);
        this.setLeftBoundingBoxIsManipulable(true);
        this.setHeightIsManipulable(true);
        this.setWidthIsManipulable(true);
        this.setAngleIsManipulable(true);

        this.setTopWhitelist(null);
        this.setLeftWhitelist(null);
        this.setTopBoundingBoxWhitelist(null);
        this.setLeftBoundingBoxWhitelist(null);
        this.setHeightWhitelist(null);
        this.setWidthWhitelist(null);
        this.setAngleWhitelist(null);

        this.setIsSelectable(true);
        this.setVisibility(0);
        this.setDeletable(true);

    }

    /**
     * Fired when the canvas object is added to a canvas.
     * @param {Canvas} canvas The canvas on which the object should be displayed.
     */
    eventAddedToCanvas(canvas) {
        CanvasDebug.canvas.object.added({ canvas, newAddedObject: this });
    }

    // Getter & Setter Methods

    /**
     * Short form for setting properties, defining values in an object instead of calling the individual getter methods.
     * @param object
     */
    set(object) {

        if (typeof object === 'object') {

            Object.getOwnPropertyNames(object).forEach(propertyName => {

                if ( typeof this[`set${propertyName.charAt(0).toUpperCase()}${propertyName.slice(1)}`] === 'function' ) {

                    this[`set${propertyName.charAt(0).toUpperCase()}${propertyName.slice(1)}`](
                        object[propertyName]
                    );

                }

                else {
                    console.log(`set${propertyName.charAt(0).toUpperCase()}${propertyName.slice(1)} not exists`);
                }

            });

        }

    }

    /**
     * If for the property a whitelist is configured and the value is in the whitelist returns true, if no whitelist configured the return value is also true.
     * @param {string} property
     * @param {any} value
     * @returns {boolean}
     */
    isManipulableAndWhitelistContainsValue(property, value) {

        let
            whitelist = null,
            isManipulable = true;

        if (window['w2p_admin'] === true)
            return true;

        property = property.charAt(0).toUpperCase() + property.slice(1);

        if (typeof this[`get${property}Whitelist`] === 'function')
            whitelist = this[`get${property}Whitelist`]();

        if (typeof this[`get${property}IsManipulable`] === 'function')
            isManipulable = this[`get${property}IsManipulable`]();

        // if no whitelist configured the return value is true
        if ( whitelist === null )
            return true;

        // if whitelist contains value
        if (Array.isArray(whitelist))
            if ( whitelist.contains(value) )
                return true;

        return false;
    }

    /**
     * Returns instance of matching FabricObject on the Canvas.
     * @returns {fabric.Object}
     */
    getMatchingFabricObject() {
        return this.getCanvas().getFabricObjectById( this.getId() );
    }

    getId() {
        return this._id;
    }

    setRandId() {
        this._id = 'obj_' + Math.random().toString(36).substr(2, 9) + new Date().getTime();
    }

    getIsSelectable() {
        return this._isSelectable;
    }

    setIsSelectable(value) {
        this._isSelectable = value;
    }

    getVisibility() {
        return this._visibility;
    }

    setVisibility(value) {
        this._visibility = value;
    }

    getZPosition() {
        return this._zPosition;
    }

    setZPosition(value) {
        this._zPosition = value;
    }

    getCanvas() {
        return this._canvas;
    }

    // Allows to define static hidden direct input fields for a object type.
    getDisabledOnForm() {
        return [];
    }

    getShowWidthDirectInputField() {
        return !this.getDisabledOnForm().find(disabled => disabled === 'width');
    }

    getShowHeightDirectInputField() {
        return !this.getDisabledOnForm().find(disabled => disabled === 'height');
    }

    getShowTopDirectInputField() {
        return !this.getDisabledOnForm().find(disabled => disabled === 'top');
    }

    getShowLeftDirectInputField() {
        return !this.getDisabledOnForm().find(disabled => disabled === 'left');
    }

    getShowAngleDirectInputField() {
        return !this.getDisabledOnForm().find(disabled => disabled === 'angle');
    }

    getTop() {
        return this._top;
    }

    setTop(value) {
        if ( this.isManipulableAndWhitelistContainsValue('top', value) === false )
            return;

        if (this._minBoundingBoxTop) {
            if (this.getCanvas().convertMmInPixel(this._minBoundingBoxTop) > value) {
                return;
            }
        }

        if (this._maxBoundingBoxTop) {
            if (this.getCanvas().convertMmInPixel(this._maxBoundingBoxTop) < value) {
                return;
            }
        }

        if (this._minBoundingBoxBottom) {
            if (this.getCanvas().convertMmInPixel(this._minBoundingBoxBottom) > value + this.getScaledHeight()) {
                return;
            }
        }

        if (this._maxBoundingBoxBottom) {
            if (this.getCanvas().convertMmInPixel(this._maxBoundingBoxBottom) < value + this.getScaledHeight()) {
                return;
            }
        }

        this._top = value;
    }

    getTopIsManipulable() {
        return this._topIsManipulable;
    }

    setTopIsManipulable(value) {
        this._topIsManipulable = value;
    }

    getTopWhitelist() {
        return this._topWhitelist;
    }

    setTopWhitelist(value) {
        this._topWhitelist = value;
    }

    getLeft() {
        return this._left;
    }

    setLeft(value) {
        if ( this.isManipulableAndWhitelistContainsValue('left', value) === false )
            return;

        if (this._minBoundingBoxLeft) {
            if (this.getCanvas().convertMmInPixel(this._minBoundingBoxLeft) > value) {
                this.setLeft(this.getCanvas().convertMmInPixel(this._minBoundingBoxLeft));
                return;
            }
        }

        if (this._maxBoundingBoxLeft) {
            if (this.getCanvas().convertMmInPixel(this._maxBoundingBoxLeft) < value) {
                return;
            }
        }

        if (this._minBoundingBoxRight) {
            if (this.getCanvas().convertMmInPixel(this._minBoundingBoxRight) > value + this.getScaledWidth()) {
                return;
            }
        }

        if (this._maxBoundingBoxRight) {
            if (this.getCanvas().convertMmInPixel(this._maxBoundingBoxRight) < value + this.getScaledWidth()) {
                this.setLeft(this.getCanvas().convertMmInPixel(this._maxBoundingBoxRight) - this.getScaledWidth());
                return;
            }
        }

        this._left = value;
    }

    getLeftIsManipulable() {
        return this._leftIsManipulable;
    }

    setLeftIsManipulable(value) {
        this._leftIsManipulable = value;
    }

    getLeftWhitelist() {
        return this._leftWhitelist;
    }

    setLeftWhitelist(value) {
        this._leftWhitelist = value;
    }

    getTopBoundingBox(value) {
        return this._topBoundingBox;
    }

    getTopBoundingBoxInMm(value) {
        const canvasObject = this.getMatchingFabricObject();

        if (! canvasObject)
            return null;

        const offset = canvasObject.top - canvasObject.getBoundingRect().top;

        return Math.round(this.getCanvas().convertPixelToMm(this.getTopBoundingBox()));
    }

    setTopBoundingBoxInMm(value) {

        if (isNaN(value))
            value = 0;

        const canvasObject = this.getMatchingFabricObject();
        const offset = canvasObject.top - canvasObject.getBoundingRect().top;

        const topBoundingBox = this.getCanvas().convertMmInPixel(value);
        this.setTopBoundingBox(topBoundingBox);
        this.setTop(topBoundingBox + this.getCanvas().getBoundingBox().getTop() + offset);
    }

    setTopBoundingBox(value) {
        if ( this.isManipulableAndWhitelistContainsValue('topBoundingBox', value) === false )
            return;

        this._topBoundingBox = value;
    }

    getTopBoundingBoxIsManipulable() {
        return this._topBoundingBoxIsManipulable;
    }

    setTopBoundingBoxIsManipulable(value) {
        this._topBoundingBoxIsManipulable = value;
    }

    getTopBoundingBoxWhitelist() {
        return this._topBoundingBoxWhitelist;
    }

    setTopBoundingBoxWhitelist(value) {
        this._topBoundingBoxWhitelist = value;
    }

    getLeftBoundingBox() {
        return this._leftBoundingBox;
    }

    getLeftBoundingBoxInMm() {
        const canvasObject = this.getMatchingFabricObject();

        if (! canvasObject)
            return null;

        const offset = canvasObject.left - canvasObject.getBoundingRect().left;

        return Math.round(this.getCanvas().convertPixelToMm(this.getLeftBoundingBox()));
    }

    setLeftBoundingBoxInMm(value) {

        if (isNaN(value))
            value = 0;

        const canvasObject = this.getMatchingFabricObject();
        const offset = canvasObject.left - canvasObject.getBoundingRect().left;

        const leftBoundingBox = this.getCanvas().convertMmInPixel(value);
        this.setLeftBoundingBox(leftBoundingBox);
        this.setLeft(leftBoundingBox + this.getCanvas().getBoundingBox().getLeft() + offset);

    }

    getLeftBoundingBoxIsManipulable() {
        return this._leftBoundingBoxIsManipulable;
    }

    setLeftBoundingBoxIsManipulable(value) {
        this._leftBoundingBoxIsManipulable = value;
    }

    getLeftBoundingBoxWhitelist() {
        return this._leftBoundingBoxWhitelist;
    }

    setLeftBoundingBoxWhitelist(value) {
        this._leftBoundingBoxWhitelist = value;
    }

    setLeftBoundingBox(value) {
        if ( this.isManipulableAndWhitelistContainsValue('leftBoundingBox', value) === false )
            return;

        this._leftBoundingBox = value;
    }

    getMaxBoundingBoxLeft() {
        return this._maxBoundingBoxLeft;
    }

    setMaxBoundingBoxLeft(value) {
        this._maxBoundingBoxLeft = value;
    }

    getMaxBoundingBoxTop() {
        return this._maxBoundingBoxTop;
    }

    setMaxBoundingBoxTop(value) {
        this._maxBoundingBoxTop = value;
    }

    getMaxBoundingBoxRight() {
        return this._maxBoundingBoxRight;
    }

    setMaxBoundingBoxRight(value) {
        this._maxBoundingBoxRight = value;
    }

    getMaxBoundingBoxBottom() {
        return this._maxBoundingBoxBottom;
    }

    setMaxBoundingBoxBottom(value) {
        this._maxBoundingBoxBottom = value;
    }

    getMinBoundingBoxLeft() {
        return this._minBoundingBoxLeft;
    }

    setMinBoundingBoxLeft(value) {
        this._minBoundingBoxLeft = value;
    }

    getMinBoundingBoxTop() {
        return this._minBoundingBoxTop;
    }

    setMinBoundingBoxTop(value) {
        this._minBoundingBoxTop = value;
    }

    getMinBoundingBoxRight() {
        return this._minBoundingBoxRight;
    }

    setMinBoundingBoxRight(value) {
        this._minBoundingBoxRight = value;
    }

    getMinBoundingBoxBottom() {
        return this._minBoundingBoxBottom;
    }

    setMinBoundingBoxBottom(value) {
        this._minBoundingBoxBottom = value;
    }

    getHeight() {
        return this._height;
    }

    getScaledHeight() {
        return this._height * (this._scaleY || 1);
    }

    getScaledHeightInMm() {
        return Math.round(this.getCanvas().convertPixelToMm(this.getScaledHeight()));
    }

    setScaledHeightInMm(value) {
        this.setScaleY(
            this.getCanvas().convertMmInPixel(value) / this.getHeight()
        );
    }

    setHeight(value) {
        if ( this.isManipulableAndWhitelistContainsValue('height', value) === false )
            return;

        this._height = value;
    }

    getHeightIsManipulable() {
        return this._heightIsManipulable;
    }

    setHeightIsManipulable(value) {
        this._heightIsManipulable = value;
    }

    getHeightWhitelist() {
        return this._heightWhitelist;
    }

    setHeightWhitelist(value) {
        this._heightWhitelist = value;
    }

    getWidth() {
        return this._width;
    }

    getScaledWidth() {
        return this._width * (this._scaleX || 1);
    }

    getScaledWidthInMm() {
        return Math.round(this.getCanvas().convertPixelToMm(this.getScaledWidth()));
    }

    setScaledWidthInMm(value) {
        this.setScaleX(
            this.getCanvas().convertMmInPixel(value) / this.getWidth()
        );
    }

    setWidth(value) {
        if ( this.isManipulableAndWhitelistContainsValue('width', value) === false )
            return;

        this._width = value;
    }

    getWidthIsManipulable() {
        return this._widthIsManipulable;
    }

    setWidthIsManipulable(value) {
        this._widthIsManipulable = value;
    }

    getWidthWhitelist() {
        return this._widthWhitelist;
    }

    setWidthWhitelist(value) {
        this._widthWhitelist = value;
    }

    getScaleX() {
        return this._scaleX;
    }

    setScaleX(value) {
        this._scaleX = value;
    }

    getScaleY() {
        return this._scaleY;
    }

    setScaleY(value) {
        this._scaleY = value;
    }

    getAngle() {
        return this._angle;
    }

    setAngle(value) {
        if ( this.isManipulableAndWhitelistContainsValue('angle', value) === false ) {
            console.log('Angle darf nicht gesetzt werden', value);
            return;
        }

        this._angle = value;
    }

    getDeletable() {
        return this._deletable;
    }

    setDeletable(value) {
        this._deletable = value;
    }

    getAngleIsManipulable() {
        return this._angleIsManipulable;
    }

    setAngleIsManipulable(value) {
        this._angleIsManipulable = value;
    }

    getAngleWhitelist() {
        return this._angleWhitelist;
    }

    setAngleWhitelist(value) {
        this._angleWhitelist = value;
    }

    getExceedsBoundingBox() {
        return this._exceedsBoundingBox;
    }

    setExceedsBoundingBox(value) {
        this._exceedsBoundingBox = value;
    }

    isVisibleInSidebar() {

        if (window['w2p_admin'] === true)
            return true;

        if (
            this.getWidthIsManipulable() ||
            this.getHeightIsManipulable() ||
            this.getTopIsManipulable() ||
            this.getLeftIsManipulable() ||
            this.getAngleIsManipulable()
        ) {
            return true;
        }

        return false;

    }

    getIsLocked() {
        return this._locked;
    }

    setIsLocked(value) {
        this._locked = value;
        return this;
    }

}

export default CanvasObject;